import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { Check, ExclamationMark, IconContext, Info, X } from '@phosphor-icons/react';
import { getMarketMetadata } from '../../../utils/market';
import InputError from '../InputError';
import InputHelp from '../InputHelp';
import Loader from '../Loader';
import {
  AffixSelect,
  AffixSelectOption,
  Button,
  ButtonWrapper,
  ColorPicker,
  ColorPreview,
  Input,
  InputWrapper,
  Label,
  Prefix,
  State,
  StateMessage,
  StateWrapper,
  Suffix,
  Truncate,
  Unit,
  Wrapper,
} from './wrapper';

export interface UnitSelectOptionProps {
  value?: number | string;
  displayName?: number | string | null;
  disabled?: boolean;
}

interface UnitSelectProps {
  title: string;
  id: string;
  value?: string | number;
  name?: string;
  disabled?: boolean;
  options?: UnitSelectOptionProps[];
  defaultOption?: UnitSelectOptionProps;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  register?: UseFormRegisterReturn;
}

export type InputTextStateTypeProps = 'success' | 'warning' | 'error' | 'info' | 'loading';

interface InputTextStateProps {
  type: InputTextStateTypeProps;
  message?: string;
}

export type Props = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  value?: string | number;
  disabled?: boolean;
  placeholder?: string;
  label: string;
  id?: string;
  type?: React.HTMLInputTypeAttribute | 'textnumber';
  actionOnClick?: () => void;
  actionButtonTitle?: string;
  actionTitle?: React.ReactNode;
  actionDisabled?: boolean;
  colorPicker?: boolean;
  unit?: string;
  unitSelect?: UnitSelectProps;
  currency?: boolean;
  currencyPostfix?: string;
  currencyMonthly?: boolean;
  prefix?: string;
  suffix?: string;
  prefixSelect?: UnitSelectProps;
  help?: string;
  error?: string;
  autoFocus?: boolean;
  register?: UseFormRegisterReturn;
  state?: InputTextStateProps;
};

const InputText = forwardRef(
  (
    {
      type = 'text',
      label,
      placeholder,
      id,
      disabled,
      actionOnClick,
      actionButtonTitle,
      actionTitle,
      actionDisabled,
      colorPicker,
      prefix,
      prefixSelect,
      suffix,
      unit,
      unitSelect,
      currency,
      currencyMonthly,
      currencyPostfix = '',
      help,
      error,
      autoFocus,
      pattern,
      inputMode,
      register,
      state,
      ...props
    }: Omit<Props, 'ref'>,
    forwardRef: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardRef, () => innerRef.current!, []);

    const hasAutoFocused = useRef(false);
    const { currencySymbol, currencyMonthlySymbol, currencySymbolBefore } = getMarketMetadata();
    useEffect(() => {
      if (autoFocus && !disabled && !hasAutoFocused.current) {
        hasAutoFocused.current = true;
        innerRef?.current?.focus();
      }
    }, [autoFocus, disabled]);

    return (
      <>
        <Wrapper $error={!!error} $disabled={disabled}>
          {colorPicker && props.value && (
            <ColorPicker>
              <ColorPreview style={{ color: props.value.toString() }} />
            </ColorPicker>
          )}

          {prefixSelect && (
            <AffixSelect
              $type="prefix"
              disabled={prefixSelect.disabled}
              value={prefixSelect.value}
              onChange={prefixSelect.onChange}
              title={prefixSelect.title}
              aria-label={prefixSelect.title}
              id={prefixSelect.id}
              name={prefixSelect.name}
              {...prefixSelect.register}
            >
              {prefixSelect.defaultOption && (
                <AffixSelectOption
                  key={prefixSelect.defaultOption.value}
                  value={prefixSelect.defaultOption.value}
                  disabled={prefixSelect.defaultOption.disabled}
                >
                  {prefixSelect.defaultOption.displayName}
                </AffixSelectOption>
              )}
              {prefixSelect.options?.map((option, index) => (
                <AffixSelectOption
                  key={`${option.value}-${index}`}
                  value={option.value || ''}
                  disabled={option.disabled}
                >
                  {option.displayName || option.value}
                </AffixSelectOption>
              ))}
            </AffixSelect>
          )}
          {prefix && (
            <Prefix>
              <Truncate>{prefix}</Truncate>
            </Prefix>
          )}
          <InputWrapper>
            <Input
              type={type === 'textnumber' ? 'text' : type}
              aria-label={label}
              id={id}
              placeholder={placeholder || label}
              ref={innerRef}
              disabled={disabled}
              pattern={type === 'textnumber' ? '[0-9]*' : pattern}
              inputMode={type === 'textnumber' ? 'numeric' : inputMode}
              {...props}
              {...register}
            />
            <Label htmlFor={id}>{label}</Label>
          </InputWrapper>
          {suffix && (
            <Suffix>
              <Truncate>{suffix}</Truncate>
            </Suffix>
          )}
          {currencyMonthly && (
            <Unit
              before={currencySymbolBefore}
            >{`${currencyMonthlySymbol}${currencyPostfix}`}</Unit>
          )}
          {currency && (
            <Unit before={currencySymbolBefore}>{`${currencySymbol}${currencyPostfix}`}</Unit>
          )}
          {unit && <Unit>{unit}</Unit>}
          {unitSelect && (
            <AffixSelect
              $type="suffix"
              disabled={unitSelect.disabled}
              value={unitSelect.value}
              onChange={unitSelect.onChange}
              title={unitSelect.title}
              aria-label={unitSelect.title}
              id={unitSelect.id}
              name={unitSelect.name}
              {...unitSelect.register}
            >
              {unitSelect.defaultOption && (
                <AffixSelectOption
                  key={unitSelect.defaultOption.value}
                  value={unitSelect.defaultOption.value}
                  disabled={unitSelect.defaultOption.disabled}
                >
                  {unitSelect.defaultOption.displayName}
                </AffixSelectOption>
              )}
              {unitSelect.options?.map((option, index) => (
                <AffixSelectOption
                  key={`${option.value}-${index}`}
                  value={option.value || ''}
                  disabled={option.disabled}
                >
                  {option.displayName || option.value}
                </AffixSelectOption>
              ))}
            </AffixSelect>
          )}
          {state && (
            <StateWrapper aria-hidden="true">
              {state.type === 'loading' ? (
                <Loader inlineBlock />
              ) : (
                <State $tone={state.type}>
                  <IconContext.Provider
                    value={{
                      size: 14,
                      weight: 'bold',
                    }}
                  >
                    {state.type === 'error' ? (
                      <X />
                    ) : state.type === 'warning' ? (
                      <ExclamationMark />
                    ) : state.type === 'info' ? (
                      <Info />
                    ) : (
                      <Check />
                    )}
                  </IconContext.Provider>
                </State>
              )}
            </StateWrapper>
          )}
          {actionOnClick && actionTitle && (
            <ButtonWrapper>
              <Button title={actionButtonTitle} onClick={actionOnClick} disabled={actionDisabled}>
                {actionTitle}
              </Button>
            </ButtonWrapper>
          )}
        </Wrapper>
        {help && <InputHelp>{help}</InputHelp>}
        {error && <InputError>{error}</InputError>}
        {state && <StateMessage $tone={state.type}>{state.message}</StateMessage>}
      </>
    );
  },
);

export default InputText;
